import { useCallback } from "react";
import { useGetBackendUrl } from "./use-get-backend-url";
import { useAuth } from "@/providers/AuthProvider";

// Generic SortOrder for GraphQL sort operations
export const SortOrder: {
  [key: string]: "Ascending" | "Descending";
} = {
  asc: "Ascending",
  desc: "Descending",
};

export interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: Array<string | number>;
}

export interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}

export const useGraphQLFetch = () => {
  const backendUrl = useGetBackendUrl();
  const { getAccessToken, instanceId } = useAuth();

  const fetchGraphQL = useCallback(
    async <T>(query: string, variables: Record<string, unknown> = {}): Promise<GraphQLResponse<T>> => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (getAccessToken) {
        try {
          const identityToken = await getAccessToken();
          if (identityToken) {
            headers["Authorization"] = `Bearer ${identityToken}`;
          }
        } catch (error) {
          console.warn("Failed to get access token:", error);
        }
      }

      // Add Instance-Id header for multi-tenant data access
      if (instanceId) {
        headers["X-Instance-Id"] = instanceId;
      }

      const response = await fetch(`${backendUrl}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    [backendUrl, getAccessToken, instanceId],
  );

  return { fetchGraphQL, backendUrl };
};
